import { memo, useMemo } from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import type { ActivityEvent, ActivityMinute } from '../../services/team-activity.service';
import {
  type ColorBy, dayRuns, eventLabel, fmtClock, fmtDuration, weekdayOf, WEEKDAYS, areaColor, deviceColor, areaLabel, deviceLabel,
} from './team-activity.format';

const DAY = 24 * 60;
const pct = (minute: number) => `${(minute / DAY) * 100}%`;

const groupBy = <T,>(items: T[], key: (x: T) => string) => {
  const out = new Map<string, T[]>();
  for (const x of items) {
    const k = key(x);
    const list = out.get(k);
    if (list) list.push(x); else out.set(k, [x]);
  }
  return out;
};

// One marker per minute and channel: an agent can make thousands of calls a day.
const markers = (events: ActivityEvent[]) => [...groupBy(events, (e) => `${e.t.slice(11, 16)}|${e.channel}`).values()]
  .map((group) => ({ at: Number(group[0].t.slice(11, 13)) * 60 + Number(group[0].t.slice(14, 16)), channel: group[0].channel, group }));

function DayStrips({ days, minutes, events, colorBy }: {
  days: string[]; minutes: ActivityMinute[]; events: ActivityEvent[]; colorBy: ColorBy;
}) {
  const theme = useTheme();
  const byDay = useMemo(() => groupBy(minutes, (m) => m.t.slice(0, 10)), [minutes]);
  const eventsByDay = useMemo(() => groupBy(events, (e) => e.t.slice(0, 10)), [events]);
  const color = colorBy === 'area' ? areaColor : deviceColor;
  const label = colorBy === 'area' ? areaLabel : deviceLabel;

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '84px 1fr 56px', mb: 0.5 }}>
        <Box />
        <Box sx={{ position: 'relative', height: 14 }}>
          {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => (
            <Typography key={h} variant="caption" color="text.secondary" sx={{ position: 'absolute', left: pct(h * 60), fontSize: '0.65rem' }}>
              {String(h).padStart(2, '0')}
            </Typography>
          ))}
        </Box>
      </Box>
      {[...days].reverse().map((d) => {
        const mins = byDay.get(d) ?? [];
        const evs = eventsByDay.get(d) ?? [];
        return (
          <Box key={d} data-testid="day-strip" sx={{ display: 'grid', gridTemplateColumns: '84px 1fr 56px', alignItems: 'center', py: 0.25 }}>
            <Typography variant="caption" color={mins.length ? 'text.primary' : 'text.disabled'}>
              {WEEKDAYS[weekdayOf(d)]} {d.slice(5)}
            </Typography>
            <Box sx={{ position: 'relative', height: 18, borderRadius: 0.5, bgcolor: theme.palette.action.hover }}>
              {dayRuns(mins, colorBy).map((r) => (
                <Tooltip key={r.start} disableInteractive title={`${fmtClock(r.start)}–${fmtClock(r.end + 1)} · ${label(r.category)} · ${r.first.page}`}>
                  <Box sx={{
                    position: 'absolute', top: 0, bottom: 0, left: pct(r.start),
                    width: `max(2px, ${pct(r.end - r.start + 1)})`, bgcolor: color(r.category), borderRadius: '2px',
                  }} />
                </Tooltip>
              ))}
              {markers(evs).map(({ at, channel, group }) => {
                const title = `${group[0].t.slice(11, 16)} · ${group.length > 1 ? `${group.length}× ` : ''}${eventLabel(group[0])}`;
                return (
                  <Tooltip key={`${at}${channel}`} disableInteractive title={title}>
                    <Box sx={channel === 'mcp'
                      ? { position: 'absolute', left: pct(at), bottom: -3, width: 6, height: 6, ml: '-3px', borderRadius: '50%', bgcolor: 'text.primary', border: 1, borderColor: 'background.paper' }
                      : { position: 'absolute', left: pct(at), top: -3, width: 2, height: 24, ml: '-1px', bgcolor: 'error.main' }} />
                  </Tooltip>
                );
              })}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
              {mins.length ? fmtDuration(mins.length) : ''}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default memo(DayStrips);
